import DatasetIcon from "@mui/icons-material/Dataset";
import DescriptionIcon from "@mui/icons-material/Description";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import {
    Autocomplete,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import _ from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import api from "../../Services/api";
import { errorManagement } from "../../Services/errorManagement";
import { getProvidersAsync } from "../../Services/providers";
import { userStore } from "../../Services/store";
import CustomAutocomplete from "../General/CustomAutocomplete";
import { HomeAppContext } from "../Home";

export const AIPromptFormMode = {
  NEW: "new",
  EDIT: "edit",
};

export default function Form({
  mode,
  entity = null,
  errors = [],
  tags = [],
  setEntity,
  setSaveDisabled,
  readOnly = false,
}) {

  const theme = useTheme();
  const { homeDispatch } = useContext(HomeAppContext);

  const textFieldRef = useRef(null);

  const [variables, setVariables] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [provider, setProvider] = useState(entity.provider || "");
  const [isClientDropdownDisabled, setIsClientDropdownDisabled] = useState(!entity.provider);
  const [providers, setProviders] = useState([]);
  const [clientDescriptions, setClientDescriptions] = useState([]);
  const [clientDesigns, setClientDesigns] = useState([]);

  const genericDesigns = clientDesigns.filter((design) => design.prepend === "0");
  const languageSpecificDesigns = clientDesigns.filter((design) => design.prepend === "1");

  const handleChange = (value, property) => {
    setEntity({ ...entity, [property]: value });
  };

  const onProviderChange = (event) => {
    const selectedProvider = event.target.value;
    setProvider(selectedProvider);
    setIsClientDropdownDisabled(!selectedProvider);

    setEntity({
      ...entity,
      provider: selectedProvider,
      aiPromptClients: [],
      clientDescriptions: [],
      clientDesigns: [],
    });
  };

  useEffect(() => {
    let isMounted = true;

    const loadProviders = async () => {
      try {
        const data = await getProvidersAsync();
        if (isMounted) {
          setProviders(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (isMounted) {
          homeDispatch({
            type: "NOTIFICATION",
            data: { message: "Unable to load providers.", type: "error" }
          });
        }
      }
    };

    loadProviders();

    return () => {
      isMounted = false;
    };
  }, [homeDispatch]);

  const onClientChange = async (value, property) => {
    const clientIds = value.map((client) => client.id);
    setSelectedClients(clientIds);
    handleChange(value, property);

    if (value.length > 0) {
      await fetchClientDescriptionsAndDesigns(clientIds);
    } else {
      setClientDescriptions([]);
      setClientDesigns([]);
    }
  };

  const fetchClientDescriptionsAndDesigns = async (clientIds) => {
    try {
      const descriptionsResponse = await api.get(
        `/api/ai-engines/clientDescriptions?ids=${clientIds.join(",")}`
      );
      setClientDescriptions(descriptionsResponse.data);

      const designsResponse = await api.get(
        `/api/ai-engines/clientDesigns?ids=${clientIds.join(",")}`
      );
      setClientDesigns(designsResponse.data);
    } catch (error) {
      errorManagement.formErrors(error, homeDispatch);
    }
  };

  const getDatasetVariables = (datasetId) => {
    homeDispatch({
      type: "NOTIFICATION",
      data: { message: "Fetching dataset variables", loading: true },
    });
  
    api
      .get(`/api/datasets/variables/${datasetId}/dropdown`)
      .then((response) => {
        homeDispatch({
          type: "NOTIFICATION",
          data: {
            message: "Dataset variables fetched successfully",
            type: "success",
          }
        });
        
        setVariables(response.data);
      })
      .catch((error) => errorManagement.formErrors(error, homeDispatch));
  };

  const insertTextAtCursor = (text, originalValue = null) => {
    const textField = textFieldRef.current;
    if (!textField) return;
  
    const valueToInsert = originalValue ? `%${originalValue}%` : text;
    
    const startPos = textField.selectionStart;
    const endPos = textField.selectionEnd;
    const newValue =
      textField.value.substring(0, startPos) +
      valueToInsert +
      textField.value.substring(endPos);
    textField.value = newValue;
  
    setEntity({ ...entity, prompt: newValue });
    textField.selectionStart = startPos + valueToInsert.length;
    textField.selectionEnd = startPos + valueToInsert.length;
  };

  useEffect(() => {
    if (entity.dataset && entity.dataset.id !== 0 && entity.dataset !== "") {
      getDatasetVariables(entity.dataset.id);
    }
  }, [entity.dataset]);

  if (entity === null) return null;

  const renderDatasetVariables = () => {
    if (!entity.dataset || variables.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          {entity.dataset
            ? "No variables found in the selected dataset."
            : "Please select a dataset to see variables."}
        </Typography>
      );
    }
  
    return (
      <Grid container spacing={1}>
        {variables.map((variable) => (
          <Grid item key={variable.id}>
            <Chip
              disabled={readOnly}
              label={variable.displayValue || variable.text}
              size="medium"
              icon={<DatasetIcon style={{ color: "#fff" }} />}
              onClick={() => insertTextAtCursor(
                `%${variable.displayValue || variable.text}%`, 
                variable.originalValue || variable.text
              )}
              style={{ 
                backgroundColor: variable.isMapping ? "#9c27b0" : "#f57c00", 
                color: "#fff" 
              }}
              title={variable.isMapping ? `Inserts: %${variable.originalValue}%` : ""}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <Grid container spacing={4}>
        <Grid item sm={6}>
          <TextField
            disabled={readOnly}
            label="Type your prompt..."
            fullWidth
            value={entity.prompt}
            multiline
            rows={28}
            onChange={(event) => handleChange(event.target.value, "prompt")}
            inputRef={textFieldRef}
            placeholder="Start typing your AI prompt here..."
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.mode === "dark" ? "#424242" : "#fff",
                borderRadius: "10px",
                borderColor: theme.palette.mode === "dark" ? "#616161" : "#e0e0e0",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 3px 6px rgba(255, 255, 255, 0.1)"
                    : "0 3px 6px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s ease",
                "& fieldset": {
                  borderColor: theme.palette.mode === "dark" ? "#616161" : "#e0e0e0",
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.mode === "dark" ? "#fff" : "#757575",
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 0 8px rgba(255, 255, 255, 0.25)"
                      : "0 0 8px rgba(63, 81, 181, 0.25)",
                },
              },
              "& .MuiInputLabel-root": {
                color: theme.palette.mode === "dark" ? "#bdbdbd" : "#757575",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: theme.palette.primary.main,
              },
              "& .MuiInputBase-input": {
                fontSize: "16px",
                lineHeight: "1.5",
                padding: "12px",
                color: theme.palette.mode === "dark" ? "#fff" : "#000",
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -6px) scale(0.85)",
              },
            }}
          />
        </Grid>

        <Grid item sm={6}>
          <Grid container spacing={3}>
            <Grid item sm={12}>
              <TextField
                required
                label="Prompt Name"
                fullWidth
                value={entity.promptName}
                onChange={(event) => handleChange(event.target.value, "promptName")}
                disabled={readOnly}
              />
            </Grid>

            {mode === AIPromptFormMode.EDIT && <Grid item sm={12}>
              <CustomAutocomplete
                value={entity.ownerUser}
                setValue={(value) => handleChange(value, "ownerUser")}
                related={null}
                onLoading={setSaveDisabled}
                url={`/api/users/dropdown`}
                label="Users"
                placeholder="Select a user"
                noOptionsText="No user found"
                loadingText="Searching"
                margin="normal"
                liveOptions={true}
                error={_.isArray(errors.ownerUser)}
                helperText={_.isArray(errors.ownerUser) && errors.ownerUser.join()}
                disabled={userStore.user_profile.role !== "Admin"}
              />
            </Grid>}

            <Grid item sm={12}>
              <CustomAutocomplete
                value={entity.aiEngines}
                setValue={(value) => handleChange(value, "aiEngines")}
                related={null}
                onLoading={setSaveDisabled}
                url={`/api/ai-engines/dropdown`}
                label="AI Engines"
                placeholder="Select an engine"
                noOptionsText="No AI engine found"
                loadingText="Searching"
                margin="normal"
                multiple={true}
                liveOptions={false}
                error={_.isArray(errors.aiEngines)}
                helperText={_.isArray(errors.aiEngines) && errors.aiEngines.join()}
                disableClearable
                disabled={readOnly}
              />
            </Grid>

            <Grid item sm={12}>
              <FormControl fullWidth>
                <InputLabel disabled={readOnly} id="provider-label">
                  Client Provider
                </InputLabel>
                <Select
                  labelId="provider-label"
                  id="provider"
                  value={provider}
                  label="Provider"
                  onChange={onProviderChange}
                  disabled={readOnly}
                >
                  {providers
                    .filter((providerOption) => providerOption?.isSelectable !== false)
                    .map((providerOption) => (
                    <MenuItem
                      key={providerOption.key || providerOption.id || providerOption.name}
                      value={providerOption.key || providerOption.id}
                    >
                      {providerOption.name || providerOption.key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item sm={12}>
              <CustomAutocomplete
                value={entity.aiPromptClients}
                setValue={(value) => onClientChange(value, "aiPromptClients")}
                related={null}
                onLoading={setSaveDisabled}
                url={`/api/clients/clients?provider=${entity.provider}`}
                label={"Clients"}
                placeholder={"Select a client"}
                noOptionsText={"No client found"}
                loadingText={"Searching"}
                margin={"normal"}
                multiple={true}
                liveOptions={true}
                disabled={isClientDropdownDisabled || readOnly}
                error={_.isArray(errors.aiPromptClients)}
                helperText={
                  _.isArray(errors.aiPromptClients) && errors.aiPromptClients.join()
                }
                disableClearable={true}
              />
            </Grid>

            <Grid item sm={12}>
              <InputLabel disabled={readOnly}>Client Descriptions</InputLabel>
              {clientDescriptions.length > 0 ? (
                <Grid container spacing={1}>
                  {clientDescriptions.map((description) => (
                    <Grid item key={description.id}>
                      <Chip
                        disabled={readOnly}
                        label={description.text}
                        size="medium"
                        icon={<DescriptionIcon style={{ color: "#fff" }} />}
                        onClick={() => insertTextAtCursor(`%${description.text}%`)}
                        style={{ backgroundColor: "#1976d2", color: "#fff" }}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {selectedClients.length === 0
                    ? "Please select a client to see client descriptions."
                    : "No client descriptions found for the selected client."}
                </Typography>
              )}
            </Grid>

            <Grid item sm={12}>
              <InputLabel disabled={readOnly}>Generic Style Guides</InputLabel>
              {genericDesigns.length > 0 ? (
                <Grid container spacing={1}>
                  {genericDesigns.map((design) => (
                    <Grid item key={design.id}>
                      <Chip
                        disabled={readOnly}
                        label={design.text}
                        size="medium"
                        icon={<DesignServicesIcon style={{ color: "#fff" }} />}
                        onClick={() => insertTextAtCursor(`%${design.text}%`)}
                        style={{ backgroundColor: "#388e3c", color: "#fff" }}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {selectedClients.length === 0
                    ? "Please select a client to see generic style guides."
                    : "No generic style guides found for the selected client."}
                </Typography>
              )}
            </Grid>

            <Grid item sm={12}>
              <InputLabel disabled={readOnly}>Language-Specific Style Guides</InputLabel>
              {languageSpecificDesigns.length > 0 ? (
                <Grid container spacing={1}>
                  <Grid item key="srcLang">
                    <Chip
                      disabled={readOnly}
                      label="srcLangStyleGuide"
                      size="medium"
                      icon={<DesignServicesIcon style={{ color: "#fff" }} />}
                      onClick={() => insertTextAtCursor("%srcLangStyleGuide%")}
                      style={{ backgroundColor: "#388e3c", color: "#fff" }}
                    />
                  </Grid>
                  <Grid item key="trgLang">
                    <Chip
                      disabled={readOnly}
                      label="trgLangStyleGuide"
                      size="medium"
                      icon={<DesignServicesIcon style={{ color: "#fff" }} />}
                      onClick={() => insertTextAtCursor("%trgLangStyleGuide%")}
                      style={{ backgroundColor: "#388e3c", color: "#fff" }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {selectedClients.length === 0
                    ? "Please select a client to see language-specific style guides."
                    : "No language-specific style guides found for the selected client."}
                </Typography>
              )}
            </Grid>

            <Grid item sm={12}>
              <CustomAutocomplete
                disabled={readOnly}
                value={entity.dataset}
                setValue={(value) => {
                  if (value !== "") {
                    handleChange(value, "dataset");
                  }
                }}
                related={null}
                onLoading={setSaveDisabled}
                url={"/api/datasets/dropdown"}
                label="Datasets"
                placeholder="Select a dataset"
                noOptionsText="No datasets found"
                loadingText="Searching"
                multiple={false}
                liveOptions={false}
                error={_.isArray(errors.dataset)}
                helperText={_.isArray(errors.dataset) && errors.dataset.join()}
                disableClearable
              />
            </Grid>

            <Grid item sm={12}>
              <InputLabel disabled={readOnly}>Dataset Variables</InputLabel>
              {renderDatasetVariables()}
            </Grid>

            <Grid item sm={12}>
              <Autocomplete
                disabled={readOnly}
                freeSolo
                multiple
                disableClearable
                value={entity.tags}
                onChange={(event, value) => handleChange(value, "tags")}
                options={tags.map((tag) => tag.text)}
                renderInput={(params) => (
                  <TextField {...params} label="Prompt Tags" InputProps={{ ...params.InputProps, type: "search" }} />
                )}
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                disabled={readOnly}
                label="Prompt Description"
                fullWidth
                value={entity.description}
                multiline
                rows={4}
                onChange={(event) => handleChange(event.target.value, "description")}
              />
            </Grid>

            <Grid item sm={12}>
              <InputLabel disabled={readOnly}>Temperature: {entity.temperature}</InputLabel>
              <Slider
                disabled={readOnly}
                value={entity.temperature}
                min={0.0}
                step={0.1}
                max={1}
                onChange={(event, newValue) => handleChange(newValue, "temperature")}
                valueLabelDisplay="auto"
                aria-labelledby="non-linear-slider"
              />
            </Grid>

            <Grid item sm={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={readOnly}
                    checked={entity.isJson}
                    onChange={(event) =>
                      handleChange(event.target.checked, "isJson")
                    }
                  />
                }
                label="Output as JSON"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}
